/*
 * Design: Swiss Precision × 和の余白
 * Brand: eightone (#E03030 accent, #1A1A1A headings, Noto Sans JP)
 * CTA導線: 「無料相談を予約する」→ サイト内 #contact セクションへスクロール
 * Mobile: タッチターゲット48px、余白最適化、フォントサイズ調整
 */

import { motion } from "framer-motion";
import { ArrowRight, ChevronDown } from "lucide-react";
import { IMAGES } from "@/lib/images";
import { scrollToContactForm } from "@/hooks/useScrollToContact";

export function HeroSection() {
  return (
    <section className="relative pt-16 sm:pt-20 lg:pt-24 overflow-hidden">
      {/* Background image with overlay */}
      <div className="absolute inset-0">
        <img
          src={IMAGES.hero}
          alt="AIエージェントによる業務自動化を推進するオフィス風景"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-white via-white/95 to-white/60 max-sm:from-white max-sm:via-white/90 max-sm:to-white/75" />
      </div>

      <div className="container relative z-10 py-12 sm:py-16 lg:py-32">
        <div className="max-w-2xl">
          {/* Tag */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="inline-flex items-center gap-2 px-3 sm:px-4 py-1.5 bg-[#F9F9F9] border border-[#E0E0E0] rounded-sm mb-6 sm:mb-8"
          >
            <span className="w-1.5 h-1.5 rounded-full bg-[#E03030]" />
            <span className="text-[10px] sm:text-xs font-medium text-[#333333] tracking-wider uppercase" style={{ fontFamily: "'Inter', sans-serif" }}>
              Antigravity × Workspace MCP
            </span>
          </motion.div>

          {/* Heading */}
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.15 }}
            className="text-[1.75rem] sm:text-4xl lg:text-5xl xl:text-[3.5rem] font-bold leading-[1.3] sm:leading-tight text-[#1A1A1A] mb-4 sm:mb-6"
          >
            いつもの業務が、
            <br />
            <span className="text-[#E03030]">勝手に片づく。</span>
          </motion.h1>

          {/* Subtitle */}
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="text-sm sm:text-base lg:text-lg text-[#333333] leading-relaxed mb-8 sm:mb-10 max-w-xl"
          >
            AIエージェントが、Google Workspaceの業務データをリアルタイムに読み取り・判断・実行。
            メール、カレンダー、スプレッドシート——日常業務の自動化を、構想から定着まで伴走します。
          </motion.p>

          {/* CTA Buttons */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.45 }}
            className="flex flex-col sm:flex-row gap-3 sm:gap-4"
          >
            <button
              onClick={() => scrollToContactForm()}
              className="inline-flex items-center justify-center gap-2 px-6 sm:px-8 py-4 bg-[#E03030] text-white font-bold text-sm rounded hover:bg-[#c72828] transition-colors duration-200 active:scale-[0.98]"
            >
              無料相談を予約する
              <ArrowRight size={16} />
            </button>
            <a
              href="#services"
              className="inline-flex items-center justify-center gap-2 px-6 sm:px-8 py-4 bg-transparent border-2 border-[#1A1A1A] text-[#1A1A1A] font-bold text-sm rounded hover:bg-[#1A1A1A] hover:text-white transition-colors duration-200 active:scale-[0.98]"
            >
              サービスを見る
            </a>
          </motion.div>

          {/* Trust badges */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.6 }}
            className="mt-8 sm:mt-12 flex items-center gap-4 sm:gap-6 text-[11px] sm:text-xs text-[#666666]"
          >
            <span>Google Workspace 認定パートナー連携</span>
            <span className="w-px h-4 bg-[#E0E0E0]" />
            <span>補助金活用実績多数</span>
          </motion.div>
        </div>
      </div>

      {/* Scroll indicator - hidden on very small screens */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.6, delay: 0.8 }}
        className="absolute bottom-4 sm:bottom-6 left-1/2 -translate-x-1/2 z-10 hidden sm:block"
      >
        <motion.div
          animate={{ y: [0, 8, 0] }}
          transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut" }}
          className="flex flex-col items-center gap-1"
        >
          <span className="text-[10px] tracking-widest uppercase text-[#666666]" style={{ fontFamily: "'Inter', sans-serif" }}>
            Scroll
          </span>
          <ChevronDown size={16} className="text-[#666666]" />
        </motion.div>
      </motion.div>
    </section>
  );
}
